// Finance > Dashboard — AR + AP + Commissions rollup landing.
//
// Mirrors commonsku §17 Finance Dashboard. Three rows:
//   1. AR (Accounts Receivable) — total outstanding + 4 aging buckets
//   2. AP (Accounts Payable) — total outstanding + pending review + 3
//      due-status buckets (current / due_soon / overdue)
//   3. Commissions — estimated / earned / paid totals
//
// Each tile is a Link to /finance/invoicing or /finance/billing with
// the appropriate filter pre-applied. Hot (non-zero) AP/AR tiles get
// rust tinting; current-state tiles stay neutral.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import { api } from "@/lib/api";

interface BucketCell {
  count: number;
  amount: string;
}

interface FinanceDashboardResponse {
  arSummary: {
    totalOpenCount: number;
    totalOpenAmount: string;
    /** Not due yet (due = last delivery + terms). */
    current: BucketCell;
    past1to30: BucketCell;
    past31to60: BucketCell;
    past61to90: BucketCell;
    pastOver90: BucketCell;
  };
  apSummary: {
    totalOpenCount: number;
    totalOpenAmount: string;
    pendingReviewCount: number;
    current: BucketCell;
    dueSoon: BucketCell;
    overdue: BucketCell;
  };
  commissionsSummary: {
    estimated: BucketCell;
    earned: BucketCell;
    paid: BucketCell;
  };
  fetchedAt: string;
}

function formatCurrency(amount: string | number): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  if (Number.isNaN(n)) return String(amount);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function FinanceDashboardPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["finance", "dashboard"],
    queryFn: () => api<FinanceDashboardResponse>("/finance/dashboard"),
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return (
      <div className="mx-auto w-full" aria-busy="true">
        <PageHeader eyebrow="Finance / Overview" title="Every detail accounted for." intro="What's due, what needs review, and what has cleared." />
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-5"><div className="h-3 w-24 animate-pulse rounded bg-canvas" /><div className="mt-3 h-7 w-24 animate-pulse rounded bg-canvas" /></div>
          ))}
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="mx-auto w-full">
        <PageHeader eyebrow="Finance / Overview" title="Every detail accounted for." />
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-6 text-sm text-risk">
          We couldn't load the finance dashboard. Nothing has changed — refresh to try again.
        </div>
      </div>
    );
  }

  const { arSummary, apSummary, commissionsSummary } = data;
  // Past due = past the due date (last delivery + the client's terms).
  const pastDue = [arSummary.past1to30, arSummary.past31to60, arSummary.past61to90, arSummary.pastOver90];
  const overdueCount = pastDue.reduce((n, b) => n + b.count, 0);
  const overdueAmount = pastDue.reduce((n, b) => n + Number(b.amount), 0);
  const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

  const tasks = [
    apSummary.pendingReviewCount > 0 && {
      title: "Bills are waiting for review.",
      detail: `${plural(apSummary.pendingReviewCount, "bill", "bills")} to check against their POs`,
      badge: "Review",
      action: "Review bills",
      href: "/finance/billing?status=pending_review",
    },
    overdueCount > 0 && {
      title: "Invoices need follow-up.",
      detail: `${plural(overdueCount, "invoice", "invoices")} · ${formatCurrency(overdueAmount)} past due`,
      badge: "Past due",
      action: "Open invoices",
      href: "/finance/invoicing?view=past-due",
    },
    commissionsSummary.earned.count > 0 && {
      title: "Commissions are ready to check.",
      detail: `${plural(commissionsSummary.earned.count, "commission", "commissions")} earned · ${formatCurrency(commissionsSummary.earned.amount)}`,
      badge: null,
      action: "Review commissions",
      href: "/finance/commissions",
    },
  ].filter(Boolean) as Array<{ title: string; detail: string; badge: string | null; action: string; href: string }>;

  const metric = "flex flex-col bg-white p-5 transition-colors hover:bg-[#fafaf8]";
  const metricLabel = "text-xs text-muted";
  const metricValue = "mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] text-ink tabular-nums";
  const metricNote = "mt-1.5 text-xs text-muted";

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Finance / Overview"
        title="Every detail accounted for."
        intro="What's due, what needs review, and what has cleared."
        action={
          <Link to="/finance/invoicing" className="inline-flex h-10 items-center rounded-md bg-ink px-[14px] text-sm font-medium text-white hover:bg-ink-700">
            Review invoices
          </Link>
        }
      />

      {/* Basis + freshness (handoff W24): these are point-in-time open
          balances, not a period report. */}
      <p className="-mt-3 mb-4 text-xs text-muted">
        Open balances as of {new Date(data.fetchedAt).toLocaleString()} · not a period report ·
        receivables age from their due date (last delivery + terms), payables from the bill due date.
      </p>

      <section aria-label="Key numbers" className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/finance/invoicing" className={metric}>
          <span className={metricLabel}>Accounts receivable</span>
          <span className={metricValue}>{formatCurrency(arSummary.totalOpenAmount)}</span>
          <span className={metricNote}>{plural(arSummary.totalOpenCount, "open invoice", "open invoices")}</span>
        </Link>
        <Link to="/finance/invoicing?view=past-due" className={metric}>
          <span className={metricLabel}>Past due balance</span>
          <span className={metricValue}>{formatCurrency(overdueAmount)}</span>
          <span className={metricNote}>{plural(overdueCount, "invoice", "invoices")} · past the due date (last delivery + terms)</span>
        </Link>
        <Link to="/finance/billing?status=pending_review" className={metric}>
          <span className={metricLabel}>Bills awaiting review</span>
          <span className={metricValue}>{apSummary.pendingReviewCount.toLocaleString()}</span>
          <span className={metricNote}>{plural(apSummary.totalOpenCount, "open bill", "open bills")} · {formatCurrency(apSummary.totalOpenAmount)}</span>
        </Link>
        <Link to="/finance/commissions" className={metric}>
          <span className={metricLabel}>Eligible commissions</span>
          <span className={metricValue}>{formatCurrency(commissionsSummary.earned.amount)}</span>
          <span className={metricNote}>Earned · not yet paid</span>
        </Link>
      </section>

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,1fr)]">
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px] text-ink">Ready for your review.</h2>
          {tasks.length === 0 ? (
            <p className="text-sm text-muted">Nothing waiting. Bills, overdue invoices and commissions are all clear.</p>
          ) : (
            <ul>
              {tasks.map((t) => (
                <li key={t.href} className="flex items-center justify-between gap-4 border-b border-[#e8e8e5] py-4 first:pt-0 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">
                      {t.title}
                      {t.badge ? <span className="ml-2 inline-block rounded bg-warn-bg px-2 py-0.5 text-[11px] font-medium text-warn">{t.badge}</span> : null}
                    </p>
                    <p className="mt-1 text-xs text-muted">{t.detail}</p>
                  </div>
                  <Link to={t.href} className="inline-flex min-h-[40px] shrink-0 items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
                    {t.action}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px] text-ink">Keep the numbers clear.</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <dt className="text-ink">Booked sales</dt><dd className="text-muted">Accepted orders</dd>
            <dt className="text-ink">Invoiced revenue</dt><dd className="text-muted">Issued invoices</dd>
            <dt className="text-ink">Collected cash</dt><dd className="text-muted">Settled payments</dd>
          </dl>
          <p className="mt-4 text-xs text-muted">These are never interchangeable — period filters and accounting policy decide what each report counts.</p>
        </section>
      </div>

      {/* Ageing detail — the same figures, as compact tables. */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <AgeingTable
          title="Receivables by age"
          link={{ to: "/finance/invoicing", label: "All invoices" }}
          rows={[
            { label: "Not due yet", cell: arSummary.current, href: "/finance/invoicing?bucket=current" },
            { label: "1–30 days past due", cell: arSummary.past1to30, href: "/finance/invoicing?bucket=1-30", warn: true },
            { label: "31–60 days past due", cell: arSummary.past31to60, href: "/finance/invoicing?bucket=31-60", warn: true },
            { label: "61–90 days past due", cell: arSummary.past61to90, href: "/finance/invoicing?bucket=61-90", warn: true },
            { label: "90+ days past due", cell: arSummary.pastOver90, href: "/finance/invoicing?bucket=90%2B", warn: true },
          ]}
          noun={["invoice", "invoices"]}
        />
        <AgeingTable
          title="Payables by due date"
          link={{ to: "/finance/billing", label: "All bills" }}
          rows={[
            { label: "Current", cell: apSummary.current, href: "/finance/billing?bucket=current" },
            { label: "Due in 7 days", cell: apSummary.dueSoon, href: "/finance/billing?bucket=due_soon", warn: true },
            { label: "Overdue", cell: apSummary.overdue, href: "/finance/billing?bucket=overdue", warn: true },
          ]}
          noun={["bill", "bills"]}
        />
      </div>
      <section className="mt-6 overflow-hidden rounded-md border border-rule bg-white">
        <div className="flex items-center justify-between gap-3 px-[18px] py-4">
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Commissions</h2>
          <span className="text-xs text-muted">Estimated → Earned → Paid</span>
        </div>
        <div className="grid grid-cols-3 gap-px border-t border-rule bg-rule">
          {([["Estimated", commissionsSummary.estimated], ["Earned", commissionsSummary.earned], ["Paid", commissionsSummary.paid]] as const).map(([label, cell]) => (
            <div key={label} className="bg-white p-5">
              <span className="text-xs text-muted">{label}</span>
              <span className="mt-1 block text-xl font-medium tabular-nums text-ink">{formatCurrency(cell.amount)}</span>
              <span className="mt-1 block text-xs text-muted">{plural(cell.count, "commission", "commissions")}</span>
            </div>
          ))}
        </div>
      </section>

      <p className="mt-6 text-xs text-muted">
        Figures as of {new Date(data.fetchedAt).toLocaleString()}. Commission figures are what the
        commission engine has recorded — estimated until the client has paid and every vendor bill is in.
      </p>
    </div>
  );
}

function AgeingTable({
  title,
  link,
  rows,
  noun,
}: {
  title: string;
  link: { to: string; label: string };
  rows: Array<{ label: string; cell: BucketCell; href: string; warn?: boolean }>;
  noun: [string, string];
}) {
  return (
    <section className="overflow-hidden rounded-md border border-rule bg-white">
      <div className="flex items-center justify-between gap-3 px-[18px] py-4">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">{title}</h2>
        <Link to={link.to} className="text-xs text-ink underline underline-offset-4 hover:text-muted">{link.label}</Link>
      </div>
      <table className="w-full text-left">
        <thead className="border-y border-[#e7e7e4] bg-[#fafaf8] text-[11px] font-medium text-muted">
          <tr>
            <th scope="col" className="px-[18px] py-3 font-medium">Bucket</th>
            <th scope="col" className="px-[18px] py-3 text-right font-medium">Count</th>
            <th scope="col" className="px-[18px] py-3 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody className="text-[13px]">
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-[#e7e7e4] last:border-0 hover:bg-[#fafaf8]">
              <td className="px-[18px] py-3">
                <Link to={r.href} className="text-ink hover:underline">{r.label}</Link>
                {r.warn && r.cell.count > 0 ? <span className="ml-2 rounded bg-warn-bg px-1.5 py-0.5 text-[11px] text-warn">Attention</span> : null}
              </td>
              <td className="px-[18px] py-3 text-right tabular-nums">{r.cell.count.toLocaleString()} <span className="text-muted">{r.cell.count === 1 ? noun[0] : noun[1]}</span></td>
              <td className="px-[18px] py-3 text-right tabular-nums">{formatCurrency(r.cell.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
